import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";

import {
  Button,
  Card
} from '@lockerpm/design';

import {
  CheckOutlined,
  CloseOutlined
} from "@ant-design/icons";

import common from "../../../../utils/common";
import global from "../../../../config/global";
import paymentServices from "../../../../services/payment";

const ChoosePlanCard = (props) => {
  const { t } = useTranslation();
  const {
    planPeriod = {},
    planInfo = {},
    planTypes = {},
    selectedPeriod = {},
    onSwitchPlan = () => {},
    setSelectedPlanDuration = () => {}
  } = props;

  const plan = useSelector((state) => state.payment.plan);
  const locale = useSelector((state) => state.system.locale);

  const isLifeTimeUser = common.isLifeTimeUser();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
  }, [])

  const features = useMemo(() => {
    return [
      'unlimited_storage',
      planInfo.alias === planTypes.FREE ? 'sync_devices' : 'sync_devices_unlimited',
      'auto_fill',
      'authenticator',
      'password_generator',
      'password_health',
      'data_breach',
      'emergency_access',
      'share_passwords',
      planInfo.alias === planTypes.FREE ? 'private_email' : 'private_email_unlimited',
      'family_members'
    ]
  }, [planInfo])

  const blockedFeatures = useMemo(() => {
    return {
      pm_free: [
        'unlimited_storage',
        'password_health',
        'data_breach',
        'emergency_access',
        'share_passwords',
        'family_members'
      ],
      pm_premium: [
        'family_members'
      ],
      pm_family: []
    }
  }, []);

  const isCurrentPlan = useMemo(() => {
    if (plan?.alias === planTypes.FREE) {
      return plan?.alias === planInfo.alias
    }
    return plan?.alias === planInfo.alias && plan?.duration === selectedPeriod.duration;
  }, [planInfo, plan, selectedPeriod])

  const isPopularPlan = useMemo(() => {
    return planInfo.alias === planTypes.PREMIUM && selectedPeriod.duration === planPeriod.YEARLY;
  }, [planInfo, selectedPeriod])

  const discountPercentage = useMemo(() => {
    const fullPrice = planInfo.price.usd * 12
    const discount = fullPrice - planInfo.yearly_price.usd
    const percent = (discount * 100) / fullPrice
    if (!Number.isNaN(percent)) {
      return Math.round(percent)
    }
    return 0
  }, [planInfo])

  const isYearly = useMemo(() => {
    return selectedPeriod.duration === planPeriod.YEARLY;
  }, [planPeriod, selectedPeriod])

  const isPurchaseBtnDisabled = useMemo(() => {
    if (planInfo.alias === planTypes.FREE || isLifeTimeUser) {
      return true
    }
    if (plan?.alias === planInfo.alias) {
      if (plan?.duration === selectedPeriod.duration) {
        return !plan?.is_trailing
      }
    }
    return planInfo.price.usd <= plan.price.usd
  }, [planInfo, selectedPeriod])

  const buttonText = useMemo(() => {
    if (plan?.alias === planInfo.alias && plan?.duration === selectedPeriod.duration) {
      if (plan?.is_trailing) {
        return t('manage_plans.purchase_now')
      }
      return t('manage_plans.current_plan')
    }
    if (plan?.alias === planTypes.LIFETIME_PREMIUM && planInfo.alias === planTypes.PREMIUM) {
      return t('manage_plans.current_plan')
    }
    if (plan?.alias === planTypes.LIFETIME_FAMILY && planInfo.alias === planTypes.FAMILY) {
      return t('manage_plans.current_plan')
    }
    if (!plan?.personal_trial_applied && plan?.alias === planTypes.FREE) {
      return t('manage_plans.start_trial');
    }
    return t('manage_plans.choose_this_plan');
  }, [plan, planInfo, selectedPeriod, locale])

  const TopInfo = () => {
    return <div className="relative">
      {
        isPopularPlan && <div className="text-center gradient-bg py-2">
          <p className="text-white font-semibold tracking-wide">
            {t('manage_plans.most_popular').toUpperCase()}
          </p>
        </div>
      }
      {
        planInfo.alias !== planTypes.FREE && selectedPeriod.duration === planPeriod.YEARLY && <div
          className="bg-warning absolute right-0 top-0 pt-4 w-[50px] text-center overflow-hidden"
        >
          <div className="text-white font-semibold">
            <p>{t('manage_plans.save')}</p>
            <p>{discountPercentage}%</p>
          </div>
          <div
            className="h-[50px] w-[50px] bg-white rotate-45 translate-y-[25px]"
          />
        </div>
      }
    </div>
  }

  const CardHeader = () => {
    return <div className="flex items-center">
      <p className="font-semibold text-[18px]">
        {planInfo.name}
      </p>
      {
        isCurrentPlan && <div
          className="bg-black-200 ml-2 px-2 py-1 rounded"
        >
          <p className="text-[9px] text-black-600 leading-[12px]">
            {t('manage_plans.current_plan').toUpperCase()}
          </p>
        </div>
      }
    </div>
  }

  const CardInfo = () => {
    return <div className="mb-4">
      {
        planInfo.alias === planTypes.FREE && <p className="text-head-3 font-semibold text-primary">
          {common.formatCurrency(planInfo.price.usd, 2, '$')}
        </p>
      }
      {
        planInfo.alias !== planTypes.FREE && <p className="text-black">
          <span
            className={`${isYearly ? 'line-through text-sm text-black' : 'text-head-3 font-semibold text-primary'}`}
          >
            {common.formatCurrency(planInfo.price.usd, 2, '$')}
          </span>
          {
            isYearly && <span
              className="text-head-3 font-semibold text-primary"
            >
              {common.formatCurrency(planInfo.yearly_price.usd / 12, 2, '$')}
            </span>
          }
          <span>/ mo</span>
          {
            planInfo.max_number && <span>/
              {
                t('plans.members', {
                  count: planInfo.max_number,
                  s: planInfo.max_number <= 1 ? '' : 's'
                })
              }
            </span>
          }
          {
            !planInfo.max_number && planInfo.alias === planTypes.BUSINESS_PREMIUM && <span
            className="text-black-600"
            >
              / {t('plans.members', { count: 1, s: '' }) }
            </span>
          }
        </p>
      }
      {
        isYearly && planInfo.alias != planTypes.FREE && <p
          className="text-black"
        >
          <span className="mr-1">
            {t('manage_plans.price_12months')}
          </span>
          <span className="text-primary font-semibold">
            {common.formatCurrency(planInfo.yearly_price.usd, 2, '$')}
          </span>
        </p>
      }
      {
        isYearly && planInfo.alias === planTypes.FREE &&<div className="h-[22px]"></div>
      }
      <p className="text-black-600">
        {t(`plans.descriptions.${planInfo.alias}`)}
      </p>
    </div>
  }

  const CardList = () => {
    return <div>
      {
        features.map((feature) => <div className="flex items-center my-1" key={feature}>
          {
            blockedFeatures[planInfo.alias].includes(feature) ? <CloseOutlined
              className="text-danger"
            /> : <CheckOutlined
              className="text-primary"
            />
          }
          <p className="ml-2">
            {t(`plans.features.${feature}`)}
          </p>
        </div>)
      }
    </div>
  }

  const handleBtnClick = () => {
    if (planInfo.alias === planTypes.FREE || isLifeTimeUser) {
      return
    }
    if (plan?.alias === planInfo.alias) {
      if (plan?.duration === selectedPeriod.duration) {
        if (plan?.is_trailing) {
          onSwitchPlan(planInfo, true)
        }
        return
      }
    }
    if (!plan?.personal_trial_applied && plan?.alias === planTypes.FREE) {
      startTrial()
    } else {
      onSwitchPlan(planInfo)
    }
  }

  const startTrial = async () => {
    setLoading(true);
    await paymentServices.trial({
      trial_plan: planInfo.alias
    }).then(async () => {
      await common.getPaymentPlan();
      setSelectedPlanDuration(planPeriod.MONTHLY);
      global.pushSuccess(t('notification.success.payments.upgrade_success'))
    }).catch((error) => {
      global.pushError(error)
    })
    setLoading(false)
  }

  return (
    <div className={`choose-plan-card ${!isPopularPlan ? 'popular-plan' : ''}`}>
      <Card
        className={`rounded-xl hover:border-primary overflow-hidden h-full ${!isPopularPlan ? 'lg:mt-[38px]' : ''}`}
        bodyStyle={{
          padding: 0,
          height: '100%'
        }}
      >
        <TopInfo />
        <div className="p-6 lg:flex lg:flex-col lg:justify-between" style={{ height: isPopularPlan ? 'calc(100% - 38px)' : '100%' }}>
          <CardHeader />
          <CardInfo />
          <CardList />
          <Button
            className="w-full mt-4"
            type={isPurchaseBtnDisabled ? 'default' : 'primary'}
            disabled={isPurchaseBtnDisabled}
            loading={loading}
            size="large"
            onClick={() => handleBtnClick()}
          >
            {buttonText}
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default ChoosePlanCard;